import fetchExpresso from 'utility/fetchExpresso';
import { stripeRequirementLookup } from './StripeOnboardingUtilities';

export async function getHostConnectDetails(hostId) {
    try {
        const response = await fetchExpresso(`/apiv2/hosts/${hostId}`);

        if (response.status === 200) {
            const host = await response.json();
            return {
                hostId: host.host_id,
                connectId: host.connect_id,
                customOnboardingSupported: host.custom_onboarding_supported,
            };
        } else {
            throw new Error('Something went wrong on our end. Please try again later.');
        }
    } catch (error) {
        throw new Error('There was a problem fetching host details');
    }
}

export async function getBusinessDetails(hostId, connectId) {
    try {
        const response = await fetchExpresso(`/apiv2/hosts/${hostId}/connect/${connectId}`);

        if (response.status === 200) {
            const { account } = await response.json();

            const business = new BusinessDetails(account);
            const requirements = new Requirements(account.requirements);

            return { business, requirements };
        } else if (response.status === 400) {
            throw new Error('Please review your business details');
        } else {
            throw new Error('Something went wrong on our end. Please try again later.');
        }
    } catch (error) {
        throw new Error('There was a problem fetching your business details');
    }
}

export async function submitBusinessDetails(hostId, connectId, businessDetails) {
    try {
        const response = await fetchExpresso(`/apiv2/hosts/${hostId}/connect/${connectId}`, {
            method: 'PUT',
            body: businessDetails,
        });

        if (response.status === 200) {
            const { account } = await response.json();

            const business = new BusinessDetails(account);
            const requirements = new Requirements(account.requirements);

            return { business, requirements };
        } else if (response.status === 400) {
            throw new Error('Please review your business details');
        } else {
            throw new Error('Something went wrong on our end. Please try again later.');
        }
    } catch (error) {
        throw new Error('There was a problem updating your business details');
    }
}

export async function acceptTermsOfService(hostId, connectId) {
    try {
        const response = await fetchExpresso(
            `/apiv2/hosts/${hostId}/connect/${connectId}/terms_of_service`,
            {
                method: 'PUT',
            },
        );

        if (response.status === 200) {
            const { account } = await response.json();

            const business = new BusinessDetails(account);
            const requirements = new Requirements(account.requirements);

            return { business, requirements };
        } else if (response.status === 400) {
            throw new Error('Please review your business details');
        } else {
            throw new Error('Something went wrong on our end. Please try again later.');
        }
    } catch (error) {
        throw new Error('There was a problem updating your business details');
    }
}

export async function createRepresentative(hostId, connectId, values) {
    try {
        const response = await fetchExpresso(
            `/apiv2/hosts/${hostId}/connect/${connectId}/representative`,
            {
                method: 'POST',
                body: values,
            },
        );

        if (response.status === 200) {
            const { persons } = await response.json();
            return persons.map((person) => new Person(person));
        } else if (response.status === 400) {
            throw new Error('Please review your representative details');
        } else {
            throw new Error('Something went wrong on our end. Please try again later.');
        }
    } catch (error) {
        throw new Error('There was a problem updating your representative details');
    }
}

export async function updateRepresentative(hostId, connectId, personId, values) {
    try {
        const response = await fetchExpresso(
            `/apiv2/hosts/${hostId}/connect/${connectId}/representative/${personId}`,
            {
                method: 'PUT',
                body: values,
            },
        );

        if (response.status === 200) {
            const { persons } = await response.json();
            return persons.map((person) => new Person(person));
        } else if (response.status === 400) {
            throw new Error('Please review your representative details');
        } else {
            throw new Error('Something went wrong on our end. Please try again later.');
        }
    } catch (error) {
        throw new Error('There was a problem updating your representative details');
    }
}

export async function listPersons(hostId, connectId) {
    try {
        const response = await fetchExpresso(`/apiv2/hosts/${hostId}/connect/${connectId}/persons`);

        if (response.status === 200) {
            const { persons } = await response.json();
            return persons.map((person) => new Person(person));
        } else if (response.status === 400) {
            throw new Error('Please review your details');
        } else {
            throw new Error('Something went wrong on our end. Please try again later.');
        }
    } catch (error) {
        throw new Error('There was a problem adding your director');
    }
}

export async function addPerson(hostId, connectId, person) {
    try {
        const response = await fetchExpresso(
            `/apiv2/hosts/${hostId}/connect/${connectId}/persons`,
            {
                method: 'POST',
                body: person,
            },
        );

        if (response.status === 200) {
            const { persons } = await response.json();
            return persons.map((person) => new Person(person));
        } else if (response.status === 400) {
            throw new Error('Please review your details');
        } else {
            throw new Error('Something went wrong on our end. Please try again later.');
        }
    } catch (error) {
        throw new Error('There was a problem adding your director');
    }
}

export async function updatePerson(hostId, connectId, personId, values) {
    try {
        const response = await fetchExpresso(
            `/apiv2/hosts/${hostId}/connect/${connectId}/persons/${personId}`,
            {
                method: 'PUT',
                body: values,
            },
        );

        if (response.status === 200) {
            const { persons } = await response.json();
            return persons.map((person) => new Person(person));
        } else if (response.status === 400) {
            throw new Error('Please review your details');
        } else {
            throw new Error('Something went wrong on our end. Please try again later.');
        }
    } catch (error) {
        throw new Error('There was a problem editing this director');
    }
}

export async function deletePerson(hostId, connectId, personId) {
    try {
        const response = await fetchExpresso(
            `/apiv2/hosts/${hostId}/connect/${connectId}/persons/${personId}`,
            {
                method: 'DELETE',
            },
        );

        if (response.status === 200) {
            const { persons } = await response.json();
            return persons.map((person) => new Person(person));
        } else if (response.status === 400) {
            throw new Error('Please review your details');
        } else {
            throw new Error('Something went wrong on our end. Please try again later.');
        }
    } catch (error) {
        throw new Error('There was a problem deleting your director');
    }
}

export async function listBankAccounts(hostId, connectId) {
    try {
        const response = await fetchExpresso(
            `/apiv2/hosts/${hostId}/connect/${connectId}/external_accounts`,
        );

        if (response.status === 200) {
            const { externalAccounts } = await response.json();
            if (Array.isArray(externalAccounts)) {
                return externalAccounts.map((account) => new BankAccount(account));
            } else {
                throw new Error('Something went wrong on our end. Please try again later.');
            }
        } else if (response.status === 400) {
            throw new Error('Please review your details');
        } else {
            throw new Error('Something went wrong on our end. Please try again later.');
        }
    } catch (error) {
        throw new Error('There was a problem adding your banking details');
    }
}

export async function addBankAccount(hostId, connectId, externalAccount) {
    try {
        const response = await fetchExpresso(
            `/apiv2/hosts/${hostId}/connect/${connectId}/external_accounts`,
            {
                method: 'POST',
                body: externalAccount,
            },
        );

        if (response.status === 200) {
            const { externalAccounts } = await response.json();
            return externalAccounts.map((account) => new BankAccount(account));
        } else if (response.status === 400) {
            throw new Error('Please review your details');
        } else {
            throw new Error('Something went wrong on our end. Please try again later.');
        }
    } catch (error) {
        throw new Error('There was a problem adding your banking details');
    }
}

export async function updateBankAccount(hostId, connectId, externalAccountId, externalAccount) {
    try {
        const response = await fetchExpresso(
            `/apiv2/hosts/${hostId}/connect/${connectId}/external_accounts/${externalAccountId}`,
            {
                method: 'PUT',
                body: externalAccount,
            },
        );

        if (response.status === 200) {
            const { externalAccounts } = await response.json();
            return externalAccounts.map((account) => new BankAccount(account));
        } else if (response.status === 400) {
            throw new Error('Please review your details');
        } else {
            throw new Error('Something went wrong on our end. Please try again later.');
        }
    } catch (error) {
        throw new Error('There was a problem adding your banking details');
    }
}

export async function deleteBankAccount(hostId, connectId, externalAccountId) {
    try {
        const response = await fetchExpresso(
            `/apiv2/hosts/${hostId}/connect/${connectId}/external_accounts/${externalAccountId}`,
            {
                method: 'DELETE',
            },
        );

        if (response.status === 200) {
            const { externalAccounts } = await response.json();
            return externalAccounts.map((account) => new BankAccount(account));
        } else if (response.status === 400) {
            throw new Error('Please review your details');
        } else {
            throw new Error('Something went wrong on our end. Please try again later.');
        }
    } catch (error) {
        throw new Error('There was a problem deleting your banking details');
    }
}

class BusinessDetails {
    // Business Types and Structures

    // individual
    //  null

    // company
    //  private_corporation, private_partnership, sole_proprietorship

    // non_profit
    //  null, registered_charity

    constructor(account) {
        this.id = account.id;
        this.businessType = account.business_type;
        this.structure = account.company?.structure || null;
        this.streetAddress = account.company?.address?.line1;
        this.unitNumber = account.company?.address?.line2;
        this.city = account.company?.address?.city;
        this.postalCode = account.company?.address?.postal_code;
        this.province = account.company?.address?.state;
        this.legalName = account.company?.name;
        this.phoneNumber = account.company?.phone;
        this.registration = account.company?.registration_number;
        this.taxIdProvided = account.company?.tax_id_provided;
        this.name = account.business_profile?.name;
        this.description = account.business_profile?.product_description;
        this.website = account.business_profile?.url;
        this.tosAccepted = Boolean(account.tos_acceptance.date);
        this.chargesEnabled = account.charges_enabled;
        this.payoutsEnabled = account.payouts_enabled;
        this.onboardComplete = account.details_submitted;
        this.errors = account.requirements.errors || [];
        this.pending = account.requirements.pending_verification || [];

        this.complete = false;

        if (
            this.streetAddress ||
            this.unitNumber ||
            this.postalCode ||
            this.province ||
            this.legalName ||
            this.phoneNumber ||
            this.registration ||
            this.name ||
            this.description ||
            this.website
        ) {
            this.complete = true;
        }

        this.requirements = []; // We include all requirements (past, present, future) due

        const allRequirements = Array.from(account.requirements.past_due || [])
            .concat(account.requirements.currently_due || [])
            .concat(account.requirements.eventually_due || []);

        for (const req of allRequirements) {
            if (this.requirements.includes(req)) {
                continue;
            } else {
                this.requirements.push(req);
            }
        }
    }
}

class Person {
    constructor(params) {
        this.id = params.id;
        this.firstName = params.first_name;
        this.lastName = params.last_name;
        this.email = params.email;
        this.phone = params.phone ? params.phone.slice(params.phone.length - 10) : null; // Removes country codes (we assume canada for now)
        this.director = params.relationship.director;
        this.representative = params.relationship.representative;
        this.owner = params.relationship.owner;
        this.title = params.relationship.title;
        // Only for some Person records
        this.city = params.address?.city;
        this.address = params.address?.line1;
        this.unit = params.address?.line2;
        this.postal = params.address?.postal_code;
        this.province = params.address?.state;
        this.dob = null;
        if (params.dob?.year && params.dob?.month && params.dob?.day) {
            this.dob =
                params.dob.year +
                '-' +
                String(params.dob.month).padStart(2, '0') +
                '-' +
                String(params.dob.day).padStart(2, '0');
        }
    }
}

class BankAccount {
    constructor(params) {
        this.id = params.id;
        this.bankName = params.bank_name || 'BANK ACCOUNT';
        this.currency = params.currency.toUpperCase();
        this.defaultForCurrency = params.default_for_currency;
        this.transitNumber = params.routing_number.slice(0, 5);
        this.institutionNumber = params.routing_number.slice(5, 8);
        this.last4 = params.last4;
    }
}

class Requirements {
    constructor(params) {
        const past_due = params.past_due || [];
        const currently_due = params.currently_due || [];
        const eventually_due = params.eventually_due || [];
        const errors = params.errors || [];
        const pending_verification = params.pending_verification || [];

        const requirements_due = new Set(
            Array.from(past_due || [])
                .concat(currently_due || [])
                .concat(eventually_due || []),
        );

        this.due = {
            count: 0,
            company: [],
            persons: [],
            risks: [],
            other: [], // tos goes here
        };

        this.pending = {
            count: 0,
            company: [],
            persons: [],
            risks: [],
            other: [], // tos goes here
        };

        this.tosRequired = false;

        // ***** Process Requirements ***** //

        for (const requirement of requirements_due) {
            const { title, category } = stripeRequirementLookup(requirement);

            if (category === 'company') {
                this.due.company.push({ code: requirement, title, message: null });
                this.due.count += 1;
            } else if (category === 'person') {
                const personId = requirement.split('.')[0];

                const newError = { code: requirement, title, message: null };

                const existingPerson = this.due.persons.findIndex(
                    (person) => person.personId === personId,
                );

                if (existingPerson === -1) {
                    this.due.persons.push({ personId: personId, errors: [newError] });
                    this.due.count += 1;
                } else {
                    this.due.persons[existingPerson].errors.push(newError);
                    this.due.count += 1;
                }
            } else if (category === 'risk') {
                this.due.risks.push({ code: requirement, title, message: null });
                this.due.count += 1;
            } else if (category === 'documents') {
                this.due.other.push({ code: requirement, title, message: requirement });
                this.due.count += 1;
            } else if (category === 'tos') {
                // put tos in other
                this.tosRequired = true;

                const existingReq = this.due.other.findIndex(
                    (req) => req.code === 'tos_acceptance',
                );

                // We condense tos_acceptance.date and tos_acceptance.ip into one requirement with code: tos_acceptance
                if (existingReq === -1) {
                    this.due.other.push({
                        code: 'tos_acceptance',
                        title,
                        message:
                            'There was a change on your account, so the service agreement must be accepted again',
                    });
                    this.due.count += 1;
                }
            } else {
                this.due.other.push({ code: requirement, title, message: null });
                this.due.count += 1;
            }
        }

        // ***** Add Error Messages to Requirements ***** //

        for (const e of errors) {
            // Note: error.requirement corresponds to requirement.code
            //       error.code is a unique code for the error
            const { code, reason, requirement } = e;

            const { title, category } = stripeRequirementLookup(requirement);

            // Company Error
            if (category === 'company') {
                const existingReq = this.due.company.findIndex((req) => req.code === requirement);

                if (existingReq === -1) {
                    this.due.company.push({ code, title, message: reason });
                    this.due.count += 1;
                } else {
                    this.due.company[existingReq].message = reason;
                }
            }

            // Individual Error
            else if (category === 'person') {
                const personId = requirement.split('.')[0];

                const existingPerson = this.due.persons.findIndex(
                    (person) => person.personId === personId,
                );
                let existingReq = -1;

                if (existingPerson !== -1) {
                    existingReq = this.due.persons[existingPerson].errors.findIndex(
                        (req) => req.code === requirement,
                    );
                }

                const newError = { code: requirement, title, message: reason };

                // Person + Requirement Exists
                // >> Update Message
                if (existingPerson !== -1 && existingReq !== -1) {
                    this.due.persons[existingPerson].errors[existingReq].message = reason;
                }

                // Person Exists, Requirement Does Not
                // >> Add Requirement to Person
                else if (existingPerson !== -1 && existingReq === -1) {
                    this.due.persons[existingPerson].errors.push(newError);
                    this.due.count += 1;
                }

                // Neither Requirement Nor Person Exists
                // >> Create Person and Requirement
                else {
                    this.due.persons.push({ personId: personId, errors: [newError] });
                    this.due.count += 1;
                }
            }

            // Risk Verification Error
            else if (category === 'risk') {
                const existingReq = this.due.risks.findIndex((req) => req.code === requirement);

                const newError = { code: requirement, title, message: reason };

                if (existingReq === -1) {
                    this.due.risks.push(newError);
                    this.due.count += 1;
                } else {
                    this.due.risks[existingReq].message = reason;
                }
            }

            // Documents
            else if (category === 'documents') {
                const existingReq = this.due.company.findIndex((req) => req.code === requirement);

                if (existingReq === -1) {
                    this.due.other.push({ code: requirement, title, message: reason });
                    this.due.count += 1;
                } else {
                    this.due.company[existingReq].message = reason;
                }
            }

            // Terms of Service
            else if (category === 'tos') {
                this.tosRequired = true;

                const existingReq = this.due.other.findIndex(
                    (req) => req.code === 'tos_acceptance',
                );

                if (existingReq === -1) {
                    this.due.other.push({ code: requirement, title, message: reason });
                    this.due.count += 1;
                } else {
                    this.due.other[existingReq].message = reason;
                }
            }
            // Other
            else {
                const existingReq = this.due.other.findIndex((req) => req.code === requirement);

                if (existingReq === -1) {
                    this.due.other.push({ code: requirement, title, message: reason });
                    this.due.count += 1;
                } else {
                    this.due.other[existingReq].message = reason;
                }
            }
        }

        // ***** Add Pending Verification ***** //

        for (const requirement of pending_verification) {
            const { title, category } = stripeRequirementLookup(requirement);

            if (category === 'company') {
                this.pending.company.push({ code: requirement, title, message: null });
                this.pending.count += 1;
            } else if (category === 'person') {
                const personId = requirement.split('.')[0];
                const existingPerson = this.pending.persons.findIndex(
                    (person) => person.personId === personId,
                );

                if (existingPerson === -1) {
                    this.pending.persons.push({
                        personId,
                        errors: [{ code: requirement, title, message: null }],
                    });
                    this.pending.count += 1;
                } else {
                    this.pending.persons[existingPerson].errors.push({
                        code: requirement,
                        title,
                        message: null,
                    });
                    this.pending.count += 1;
                }
            }

            // Risk Verification Error
            else if (category === 'risk') {
                this.pending.risks.push({ code: requirement, title, message: null });
                this.pending.count += 1;
            }

            // Documents
            else if (category === 'documents') {
                this.pending.company.push({ code: requirement, title, message: null });
                this.pending.count += 1;
            }

            // Terms of Service
            else if (category === 'tos') {
                // put tos in other
                const existingReq = this.pending.other.findIndex(
                    (req) => req.code === 'tos_acceptance',
                );

                // We condense tos_acceptance.date and tos_acceptance.ip into one requirement
                if (existingReq === -1) {
                    this.pending.other.push({ code: 'tos_acceptance', title, message: null });
                    this.pending.count += 1;
                }
            }

            // Other
            else {
                this.pending.other.push({ code: requirement, title, message: null });
                this.pending.count += 1;
            }
        }
    }
}
